import * as React from 'react'

const validate = (fields, validatorsToCheck, props) => {
  try {
    const errors = Object.keys(fields)
      .map(key => ({
        name: key,
        error: validatorsToCheck[key](fields[key].value, props),
      }))
      .filter(result => result.error && result.error.length)

    if (errors.length) {
      throw errors
    }

    // Return field values if there are no errors
    return Object.keys(fields).reduce((results, key) => {
      return {
        ...results,
        [key]: fields[key].value,
      }
    }, {})
  } catch (error) {
    throw error
  }
}

type FormProps = {
  onSubmit: (any) => Promise<any>
  validators: any
  fields: any
  children: any
}

type FormState = {
  isSubmitted: boolean
  isLoading: boolean
  fields: any
}

class Form extends React.Component<FormProps, FormState> {
  constructor(props) {
    super(props)
    this.state = {
      isSubmitted: false,
      isLoading: false,
      fields: this.generateFieldStateForFields(props.fields),
    }
  }

  public generateFieldStateForFields = fields => {
    return Object.keys(fields).reduce((fieldStates, key) => {
      return {
        ...fieldStates,
        [key]: {
          value: fields[key],
          error: null,
          touched: false,
          name: key,
        },
      }
    }, {})
  }

  public handleSubmit = async e => {
    // Prevent default behavior of form
    if (e && e.preventDefault) {
      e.preventDefault()
    }
    // Start submitting

    this.clearErrors()
    this.isSubmitted()

    try {
      const fieldValues = validate(
        this.state.fields,
        this.props.validators,
        this.props
      )
      this.startLoading()
      await this.props.onSubmit(fieldValues)
      this.endLoading()
    } catch (errors) {
      this.endLoading()
      this.handleErrors(errors)
    }
  }

  public isSubmitted = () => {
    this.setState({ isSubmitted: true })
  }

  public startLoading = () => {
    this.setState({ isLoading: true })
  }

  public endLoading = () => {
    this.setState({ isLoading: false })
  }

  public handleChange = (name, value) => {
    this.setState(previousState => ({
      ...previousState,
      fields: {
        ...previousState.fields,
        [name]: {
          ...previousState.fields[name],
          value,
          touched: true,
          error: null,
        },
      },
    }))
  }

  // TODO(markn): We rely heavily on django conventions to
  // decide what errors to throw and how we deal with 400s. Should make this
  // more generic at some point...
  public handleErrors = (caughtError = null) => {
    const errors = caughtError.fieldErrors
      ? Object.keys(caughtError.fieldErrors).map(name => ({
          name,
          error: caughtError.fieldErrors[name],
        }))
      : caughtError

    if (errors && errors.length) {
      errors.forEach(({ name, error }) => {
        this.setState(previousState => ({
          ...previousState,
          fields: {
            ...previousState.fields,
            [name]: {
              ...previousState.fields[name],
              error,
            },
          },
        }))
      })
      throw errors
    } else {
      throw caughtError
    }
  }

  public clearErrors = () => {
    Object.keys(this.state.fields).forEach(name => {
      this.setState(previousState => ({
        ...previousState,
        fields: {
          ...previousState.fields,
          [name]: {
            ...previousState.fields[name],
            error: null,
          },
        },
      }))
    })
  }

  public render() {
    return this.props.children(
      this.state.fields,
      this.handleChange,
      this.handleSubmit,
      this.state
    )
  }
}

export default Form
